import { useMemo } from 'react'

import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import {
  CatalogPermissionUserConfig,
  type CatalogPermissionUserConfigConstructor,
} from '@webapp/modules/purchasing/entities/CatalogPermissionUserConfig'
import {
  DEFAULT_CATALOG_CONFIG_PAGING,
  useCatalogPermissionConfigListState,
} from '@webapp/modules/purchasing/hooks/useCatalogPermissionConfigListState'
import { useListCatalogPermissionUserConfigsModel } from '../../models/useListCatalogPermissionUserConfigsModel'

export interface IUseCatalogPermissionUserConfigListPresenterParams {
  selectedUserId: Nullable<number>
  onSelectUser: (user: CatalogPermissionUserConfig) => void
}

export const useCatalogPermissionUserConfigListPresenter = ({
  selectedUserId,
  onSelectUser,
}: IUseCatalogPermissionUserConfigListPresenterParams) => {
  const {
    modelParams,
    searchInput,
    handleSearchChange,
    handleSearchSubmit,
    handlePageChange,
  } = useCatalogPermissionConfigListState()

  const { data, isLoading, error } = useListCatalogPermissionUserConfigsModel({
    search: modelParams.search || undefined,
    page: modelParams.page,
    limit: modelParams.limit,
    offset: modelParams.offset,
  })

  const items: CatalogPermissionUserConfig[] = useMemo(
    () =>
      (data?.data ?? []).map(
        (constructor: CatalogPermissionUserConfigConstructor) =>
          new CatalogPermissionUserConfig(constructor)
      ),
    [data?.data]
  )

  const paging: RepoPaginationResponse =
    data?.pagination ?? DEFAULT_CATALOG_CONFIG_PAGING
  const isError = error !== null

  return {
    data: {
      items,
      paging,
    },
    states: {
      isLoading,
      isError,
      searchValue: searchInput,
      selectedUserId,
    },
    commands: {
      handleSelectUser: onSelectUser,
      handleSearchChange,
      handleSearchSubmit,
      handlePageChange,
    },
  }
}
